/**
 * VasFlamSkillEffect — Fire tree Fork 1-A entry: a wide incantation cone.
 *
 * Not an upgrade of Conduit — a second fire active alongside it on the
 * hotbar. Fires a wider cone (45°, 3-tile range) than Conduit (20°, 2.5 tiles)
 * with higher direct-hit damage and its own resolver scope ('vas_flam').
 *
 * Applies burning DoT (1 stack per target, base 5 dps) and direct-hit
 * damage to all enemies in cone. Burning application is declarative via
 * skill-effect-declarations.json; intensity resolved by the engine.
 * Cone range/angle scale with resolver.getAdditiveEffect('vas_flam', 'rangeTiles')
 * and 'spreadDegrees' (Extended Reach adds +1 tile, +10°).
 *
 * Fizzle consumes cooldown. Out-of-range (empty cone) does not consume cooldown.
 * Breaks Hide on activation.
 *
 * Direct hit damage: 8 (placeholder — final value lives in combat config).
 * Base burning intensity: 5 dps (higher than Conduit's 4.2; unspecified in JSON).
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-066 (source-tagged DoT coexistence)
 * @see fire-spec.md § Vas Flam
 * @see design-decisions.md § Conduit Cone Angle (establishes spread progression)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface VasFlamDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  /**
   * Dispatch declarative status effects via SkillEffectDeclarationEngine.
   * The engine reads skill-effect-declarations.json and resolves
   * params/conditions — the skill no longer manages effect application.
   */
  readonly dispatchEffects: (trigger: string, targetId?: string) => void;
  /**
   * Returns entity IDs of enemies within the cone. Cone is centered on the
   * player's position, extending in the facing direction.
   */
  readonly getConeTargets: (lengthTiles: number, angleRangeDeg: number, facingAngle: number) => string[];
  /** Player's current facing angle in radians. */
  readonly getPlayerFacing: () => number;
  /** Returns true if activation succeeds (false = fizzle). */
  readonly rollSkillSuccess: (skillId: string) => boolean;
  /** Break stealth/hide on activation. */
  readonly breakHide: () => void;
  /**
   * Deal direct (non-DoT) hit damage to a target.
   * Called once per enemy hit in the cone on a successful activation.
   */
  readonly dealDamage: (targetId: string, damage: number) => void;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface VasFlamConfig {
  /** Cooldown in ms after activation or fizzle. Default: 1000 (medium tier). */
  readonly cooldownMs: number;
  /**
   * Direct hit damage per target. Placeholder value — final balance number
   * lives in combat config; Vas Flam's JSON only specifies cooldownTier.
   */
  readonly baseDirectDamage: number;
  /** Cone length in tiles. Extended Reach adds +1. */
  readonly coneLength: number;
  /** Cone full arc angle in degrees. Extended Reach adds +10°. */
  readonly coneAngle: number;
}

const DEFAULT_CONFIG: VasFlamConfig = {
  cooldownMs: 1000,              // medium cooldown tier
  baseDirectDamage: 8,           // placeholder — see combat config
  coneLength: 3,                 // 3-tile range
  coneAngle: 45,                 // 45° spread (wider than Conduit's 20°)
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class VasFlamSkillEffect implements SkillEffect {
  readonly skillId = 'vas_flam';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly missLabel = 'WHIFF';
  readonly failLabel = 'FIZZLE';

  private readonly deps: VasFlamDeps;
  private readonly config: VasFlamConfig;

  private _cooldownRemaining = 0;
  private _activations = 0;
  private _fizzles = 0;
  private _lastHitCount = 0;

  constructor(deps: VasFlamDeps, config: Partial<VasFlamConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- Observable state ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activations(): number { return this._activations; }
  get fizzles(): number { return this._fizzles; }
  get lastHitCount(): number { return this._lastHitCount; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — cooldown consumed even on fizzle
    if (!this.deps.rollSkillSuccess('vas_flam')) {
      this._fizzles++;
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Break hide before firing
    this.deps.breakHide();

    // Resolve cone upgrade bonuses (range/angle still managed by the skill)
    const bonusRange = this.deps.resolver.getAdditiveEffect('vas_flam', 'rangeTiles');
    const bonusAngle = this.deps.resolver.getAdditiveEffect('vas_flam', 'spreadDegrees');

    const resolvedRange = this.config.coneLength + bonusRange;
    const resolvedAngle = this.config.coneAngle + bonusAngle;

    // Query targets in cone
    const facingAngle = this.deps.getPlayerFacing();
    const targets = this.deps.getConeTargets(resolvedRange, resolvedAngle, facingAngle);
    this._lastHitCount = targets.length;

    // Out-of-range (no targets in cone) — fizzle; no cooldown, no stamina
    if (targets.length === 0) {
      return false;
    }

    // Apply direct damage and dispatch burning DoT per target via declaration engine
    for (const targetId of targets) {
      this.deps.dealDamage(targetId, this.config.baseDirectDamage);
      this.deps.dispatchEffects('hit', targetId);
    }

    this._activations++;
    this._cooldownRemaining = this.config.cooldownMs;
    return true;
  }

  deactivate(): void {}

  isActive(): boolean { return false; }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
  }
}
